import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/dashboard/PageHeader';
import MasterDetailGrid from '@/components/dashboard/MasterDetailGrid';
import MasterDetailEmptyState from '@/components/dashboard/MasterDetailEmptyState';
import { useTranslation } from 'react-i18next';
import ContactsStatsBar from '@/components/clients/ContactsStatsBar';
import ContactsList, {
  ContactsListSkeleton,
} from '@/components/clients/ContactsList';
import AddLeadDialog from '@/components/clients/AddLeadDialog';
import EditContactDialog from '@/components/clients/EditContactDialog';
import ContactProfileSheet from '@/components/clients/ContactProfileSheet';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  Search,
  RefreshCw,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import {
  computeContactStats,
  applyContactFilter,
} from '@/lib/clientsData';
import { useData } from '@/context/DataContext';

export default function ClientsLeadsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Shared cache (jobs + clients lifted into <DataProvider>) ────────────
  const {
    clients: contacts,
    clientsLoading,
    clientsError,
    hasLoadedClients,
    jobs,
    ensureJobs,
    ensureClients,
    refreshJobs,
    refreshClients,
    upsertClientInCache,
    removeClientFromCache,
  } = useData();

  // Lazy-load on first mount; the provider dedupes if these tables were
  // already fetched by another page in this session.
  useEffect(() => {
    ensureClients();
    ensureJobs();
  }, [ensureClients, ensureJobs]);

  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('clients');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('name-asc');
  const [profileId, setProfileId] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  // First-load only shows skeleton; subsequent visits are instant from cache.
  const loading = !hasLoadedClients && clientsLoading;
  const error = clientsError;

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([refreshClients(), refreshJobs()]);
    } catch (_) {
      /* errors surface through context state */
    } finally {
      setRefreshing(false);
    }
  }, [refreshClients, refreshJobs]);

  // Derive the active profile from the cache so cache updates are reflected
  // live in the open profile sheet.
  const profile = useMemo(
    () => (profileId ? contacts.find((c) => c.id === profileId) || null : null),
    [contacts, profileId]
  );

  // Cross-link support: /dashboard/clients-leads?contactId=<uuid> auto-opens profile.
  useEffect(() => {
    const contactId = searchParams.get('contactId');
    if (!contactId || !contacts.length) return;
    const found = contacts.find((c) => c.id === contactId);
    if (found) {
      setProfileId(found.id);
      setProfileOpen(true);
      // Switch tab to match the contact's type so the row is visible behind the sheet.
      if (found.type === 'lead') setTab('leads');
      else if (found.type === 'client') setTab('clients');
      // Drop the param from the URL so closing the sheet doesn't re-open it.
      const next = new URLSearchParams(searchParams);
      next.delete('contactId');
      setSearchParams(next, { replace: true });
    }
  }, [contacts, searchParams, setSearchParams]);

  const stats = useMemo(() => computeContactStats(contacts), [contacts]);
  const filtered = useMemo(
    () => applyContactFilter(contacts, { tab, search, sort }),
    [contacts, tab, search, sort]
  );

  // Mutation reflectors — write directly into the shared cache.
  const refreshContact = useCallback(
    (updated) => {
      if (!updated) return;
      upsertClientInCache(updated);
    },
    [upsertClientInCache]
  );
  const removeContact = useCallback(
    (removed) => {
      if (!removed) return;
      removeClientFromCache(removed.id);
      setProfileId((cur) => (cur === removed.id ? null : cur));
      setProfileOpen((cur) => (profileId === removed.id ? false : cur));
    },
    [removeClientFromCache, profileId]
  );
  const addContact = useCallback(
    (created) => {
      if (!created) return;
      upsertClientInCache(created);
      // jump to leads tab so the user sees the new lead they just created
      if (created.type === 'lead') setTab('leads');
    },
    [upsertClientInCache]
  );

  const openProfile = (contact) => {
    setProfileId(contact?.id || null);
    setProfileOpen(true);
  };
  const handleEdit = (contact) => {
    setEditTarget(contact);
    setEditOpen(true);
  };

  const tabCounts = useMemo(() => {
    const clientsCnt = contacts.filter((c) => c.type === 'client').length;
    const leadsCnt = contacts.filter((c) => c.type === 'lead').length;
    return { clients: clientsCnt, leads: leadsCnt, all: contacts.length };
  }, [contacts]);

  return (
    <div data-testid="clients-leads-page">
      <PageHeader
        eyebrow={t('clientsLeads.eyebrow')}
        title={t('clientsLeads.title')}
        subtitle={t('clientsLeads.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading || refreshing}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="contacts-refresh-button"
            >
              <RefreshCw
                size={14}
                className={`mr-2 ${refreshing ? 'animate-spin' : ''}`}
              />
              {t('clientsLeads.refresh')}
            </Button>
            <Button
              onClick={() => setAddOpen(true)}
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="contacts-add-lead-button"
            >
              <UserPlus size={14} className="mr-2" />
              {t('clientsLeads.addLead')}
            </Button>
          </div>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="contacts-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      <ContactsStatsBar stats={stats} />

      <div className="mt-5 sm:mt-6">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <TabsList
              className="bg-[hsl(var(--muted))] border border-border h-10 p-1"
              data-testid="contacts-tabs"
            >
              <TabsTrigger
                value="clients"
                className="data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:border data-[state=active]:border-[hsl(var(--primary))]/30 text-muted-foreground uppercase tracking-[0.16em] text-xs"
                data-testid="contacts-tab-clients"
              >
                <Users size={14} className="mr-1.5" />
                {t('clientsLeads.tabs.clients', { count: tabCounts.clients })}
              </TabsTrigger>
              <TabsTrigger
                value="leads"
                className="data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:border data-[state=active]:border-[hsl(var(--primary))]/30 text-muted-foreground uppercase tracking-[0.16em] text-xs"
                data-testid="contacts-tab-leads"
              >
                <UserPlus size={14} className="mr-1.5" />
                {t('clientsLeads.tabs.leads', { count: tabCounts.leads })}
              </TabsTrigger>
              <TabsTrigger
                value="all"
                className="data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:border data-[state=active]:border-[hsl(var(--primary))]/30 text-muted-foreground uppercase tracking-[0.16em] text-xs"
                data-testid="contacts-tab-all"
              >
                {t('clientsLeads.tabs.all', { count: tabCounts.all })}
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2">
              <div className="relative flex-1 lg:w-72">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('clientsLeads.searchPlaceholder')}
                  className="h-10 pl-9 pr-9 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                  data-testid="contacts-search-input"
                />
                {search ? (
                  <button
                    type="button"
                    aria-label={t('clientsLeads.clearSearch')}
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="contacts-search-clear"
                  >
                    <X size={14} />
                  </button>
                ) : null}
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger
                  className="h-10 w-[160px] bg-[hsl(var(--muted))] border-border text-foreground"
                  data-testid="contacts-sort"
                >
                  <SelectValue placeholder={t('clientsLeads.sort.label')} />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border">
                  <SelectItem value="name-asc">{t('clientsLeads.sort.nameAsc')}</SelectItem>
                  <SelectItem value="name-desc">{t('clientsLeads.sort.nameDesc')}</SelectItem>
                  <SelectItem value="value-desc">{t('clientsLeads.sort.valueDesc')}</SelectItem>
                  <SelectItem value="recent">{t('clientsLeads.sort.recent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Reuse filtered results across tabs (filter logic does the work) */}
          <div className="mt-4">
            <MasterDetailGrid
              list={
                loading ? (
                  <ContactsListSkeleton />
                ) : (
                  <ContactsList
                    contacts={filtered}
                    onOpenContact={openProfile}
                    emptyTitle={
                      search
                        ? t('clientsLeads.empty.noMatchesTitle')
                        : tab === 'leads'
                        ? t('clientsLeads.empty.noLeadsTitle')
                        : tab === 'clients'
                        ? t('clientsLeads.empty.noClientsTitle')
                        : t('clientsLeads.empty.noContactsTitle')
                    }
                    emptyDescription={
                      search
                        ? t('clientsLeads.empty.noMatchesDesc')
                        : tab === 'leads'
                        ? t('clientsLeads.empty.noLeadsDesc')
                        : tab === 'clients'
                        ? t('clientsLeads.empty.noClientsDesc')
                        : t('clientsLeads.empty.noContactsDesc')
                    }
                    onAddLead={() => setAddOpen(true)}
                  />
                )
              }
              detail={
                profile ? (
                  <ContactProfileSheet
                    contact={profile}
                    jobs={jobs}
                    open={profileOpen}
                    onOpenChange={setProfileOpen}
                    onEdit={(c) => {
                      setEditTarget(c);
                      setEditOpen(true);
                    }}
                    onChanged={refreshContact}
                    onDeleted={removeContact}
                  />
                ) : (
                  <MasterDetailEmptyState
                    Icon={Users}
                    title={t('clientsLeads.selectHintTitle')}
                    description={t('clientsLeads.selectHintDescription')}
                  />
                )
              }
            />
          </div>
        </Tabs>
      </div>

      <AddLeadDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={addContact}
      />
      <EditContactDialog
        contact={editTarget}
        open={editOpen}
        onOpenChange={setEditOpen}
        onUpdated={refreshContact}
      />
    </div>
  );
}
